import {
  ArrowLeft,
  LoaderCircle,
  MailPlus,
  ShieldCheck,
  MessageSquareText,
  UsersRound,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import AppHeader from "@/components/AppHeader";

const apiUrl = process.env.REACT_APP_BACKEND_URL;

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [feedbackError, setFeedbackError] = useState("");

  const loadDashboard = useCallback(async () => {
    const profileResponse = await fetch(`${apiUrl}/api/auth/me`, {
      credentials: "include",
    });
    if (!profileResponse.ok) {
      navigate("/login", { replace: true });
      return;
    }
    const profile = await profileResponse.json();
    if (!profile.is_owner) {
      toast.error("This dashboard is only available to the account owner.");
      navigate("/", { replace: true });
      return;
    }
    const [invitationResult, feedbackResult] = await Promise.allSettled([
      fetch(`${apiUrl}/api/pilot/invitations`, { credentials: "include" }),
      fetch(`${apiUrl}/api/feedback`, { credentials: "include" }),
    ]);
    if (
      invitationResult.status !== "fulfilled" ||
      !invitationResult.value.ok
    ) {
      throw new Error("Invitation records could not be loaded.");
    }
    setDashboard(await invitationResult.value.json());
    if (feedbackResult.status === "fulfilled" && feedbackResult.value.ok) {
      setFeedback((await feedbackResult.value.json()).feedback);
      setFeedbackError("");
    } else {
      setFeedback([]);
      setFeedbackError("Feedback activity is temporarily unavailable.");
    }
  }, [navigate]);

  useEffect(() => {
    loadDashboard()
      .catch((error) => toast.error(error.message))
      .finally(() => setIsLoading(false));
  }, [loadDashboard]);

  const sendInvitation = async (event) => {
    event.preventDefault();
    setIsSending(true);
    try {
      const response = await fetch(`${apiUrl}/api/pilot/invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          origin_url: window.location.origin,
        }),
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.detail || "Invitation could not be sent.");
      }
      setEmail("");
      toast.success(
        `Invitation sent to ${body.email}. One pilot place is reserved.`,
      );
      await loadDashboard();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="judge-app" data-testid="owner-dashboard-page">
      <AppHeader onNewRound={() => navigate("/")} isPro isOwner />
      <main className="owner-dashboard">
        <Link
          className="back-link"
          to="/"
          data-testid="owner-dashboard-back-link"
        >
          <ArrowLeft size={17} />
          Return to workspace
        </Link>
        <section className="owner-dashboard-heading section-reveal">
          <p className="eyebrow">
            <ShieldCheck size={14} /> Owner tools
          </p>
          <h1>Private pilot invitations</h1>
          <p>
            Send a one-time, email-matched link for a 72-hour Tournament Pilot
            Pass.
          </p>
        </section>
        {isLoading && (
          <div
            className="owner-dashboard-loading"
            data-testid="owner-dashboard-loading"
          >
            <LoaderCircle className="payment-spinner" size={25} />
            Loading invitations
          </div>
        )}
        {!isLoading && dashboard && (
          <>
            <section className="pilot-invite-overview section-reveal delay-one">
              <div data-testid="pilot-reserved-slots">
                <UsersRound size={22} />
                <strong>{dashboard.reserved_slots} reserved</strong>
                <span>of 25 Tournament Pilot Passes</span>
              </div>
              <div data-testid="pilot-available-slots">
                <strong>{dashboard.available_slots} available</strong>
                <span>Places reserve immediately when an email is sent.</span>
              </div>
            </section>
            <section className="pilot-invite-form-section section-reveal delay-two">
              <div>
                <p className="eyebrow">Send invitation</p>
                <h2>Reserve a place</h2>
              </div>
              <form onSubmit={sendInvitation} data-testid="pilot-invite-form">
                <label htmlFor="pilot-invite-email">Recipient email</label>
                <div className="pilot-invite-form-row">
                  <input
                    id="pilot-invite-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="judge@example.com"
                    required
                    disabled={isSending || dashboard.available_slots === 0}
                    data-testid="pilot-invite-email-input"
                  />
                  <button
                    className="primary-action"
                    disabled={isSending || dashboard.available_slots === 0}
                    data-testid="pilot-invite-send-button"
                  >
                    {isSending ? (
                      <LoaderCircle className="payment-spinner" size={17} />
                    ) : (
                      <MailPlus size={17} />
                    )}
                    {isSending ? "Sending" : "Send private link"}
                  </button>
                </div>
              </form>
            </section>
            <section className="pilot-invitation-list section-reveal delay-three">
              <div className="pilot-invitation-list-heading">
                <p className="eyebrow">Reserved places</p>
                <h2>Invitation activity</h2>
              </div>
              {dashboard.invitations.length === 0 ? (
                <p
                  className="pilot-invitation-empty"
                  data-testid="pilot-invitation-empty-state"
                >
                  No invitations have been sent yet.
                </p>
              ) : (
                <div className="pilot-invitation-table" data-testid="pilot-invitation-list">
                  {dashboard.invitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      data-testid={`pilot-invitation-${invitation.id}`}
                    >
                      <span>{invitation.email}</span>
                      <span className={`pilot-invitation-status ${invitation.status}`}>
                        {invitation.status}
                      </span>
                      <span>{new Date(invitation.sent_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="owner-feedback section-reveal delay-three">
              <div className="pilot-invitation-list-heading">
                <p className="eyebrow">
                  <MessageSquareText size={14} /> Early judge feedback
                </p>
                <h2>What to improve next</h2>
              </div>
              {feedbackError ? (
                <p className="owner-feedback-error" data-testid="owner-feedback-error">
                  {feedbackError}
                </p>
              ) : feedback.length === 0 ? (
                <p className="pilot-invitation-empty" data-testid="owner-feedback-empty-state">
                  No post-round feedback yet.
                </p>
              ) : (
                <div className="owner-feedback-list" data-testid="owner-feedback-list">
                  {feedback.map((item) => (
                    <article key={item.id} data-testid={`owner-feedback-${item.id}`}>
                      <span className={`feedback-rating-label ${item.rating}`}>
                        {item.rating.replace("_", " ")}
                      </span>
                      <p>{item.comment || "No written note."}</p>
                      <time>{new Date(item.created_at).toLocaleDateString()}</time>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}